"use client";

import React, { useState, JSX } from "react";
import logo from "@/assets/images/logos/brand/logo.webp";
import Image from "next/image";
import { icons } from "@/icons";
import { cn } from "@/lib/utils";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { DownloadButton } from "@/components/ui/DownloadButton";

// Navigation links - refined for the redesigned landing page
const navLinks = [
  { name: "Home", href: "/" },
  { name: "The Game", href: "#gamefeaturesection" },
  { name: "Get Started", href: "#howitworkssection" },
  { name: "Reviews", href: "#testimonials" },
  { name: "Team", href: "#aboutus" },
  { name: "Blog", href: "/blog" },
  { name: "Families", href: "/families", badge: "beta" },
];


export default function Navbar(): JSX.Element {
  const [isMenuOpen, setMenuOpen] = useState<boolean>(false);
  const pathname = usePathname();
  const isHomePage = pathname === "/";

  const handleNavigation = (href: string): void => {
    // Check if it's a hash link (starts with #) or a regular URL
    if (href.startsWith('#')) {
      if (isHomePage) {
        // On homepage, use smooth scroll
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "start"
          });
        }
      } else {
        // On other pages, navigate to homepage with hash
        window.location.href = `/${href}`;
      }
    } else {
      // Regular URL - navigate normally
      window.location.href = href;
    }
  };

  // Filter links based on current page
  const getVisibleLinks = () => {
    if (isHomePage) {
      // On homepage, don't show the Home link
      return navLinks.filter(link => link.href !== "/");
    }
    return navLinks;
  };

  const visibleLinks = getVisibleLinks();

  return (
    <nav className={cn("z-50 w-full absolute top-0")} style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}>
      <div className="max-w-7xl w-full py-5 px-5 mx-auto flex justify-between items-center">
        <div>
          <Link href="/" aria-label="Return to homepage">
            <Image alt="logo" className="h-[2rem] w-[7.6rem]" src={logo}></Image>
          </Link>
        </div>
        <div className="hidden lg:flex gap-1 text-black items-center">
          {visibleLinks.map((item, index) => (
            <button
              key={index}
              onClick={() => handleNavigation(item.href)}
              className={cn(
                "px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 flex items-center",
                "hover:bg-black/5 active:scale-95",
                item.badge && "relative"
              )}
              aria-label={`Navigate to ${item.name} section`}
            >
              {item.name}
              {item.badge && (
                <span className="absolute left-1/2 -translate-x-1/2 top-full -mt-2.5 text-[10px] font-medium text-black/40">
                  {item.badge}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="lg:flex gap-4 hidden">
          <DownloadButton variant="appstore" size="compact" singleLine label="Download" />
        </div>
        <div className="lg:hidden flex gap-4 items-center">
          <MenuToggle isMenuOpen={isMenuOpen} setMenuOpen={setMenuOpen} />
        </div>
      </div>
      {isMenuOpen && (
        <div
          id="mobile-menu"
          className="flex p-5 lg:hidden h-full flex-col gap-2 w-[85%] text-black mx-auto bg-[#FFFFF0] pt-4 pb-6 rounded-2xl text-center shadow-lg border border-black/5"
          role="menu"
          aria-label="Mobile navigation menu"
        >
          {visibleLinks.map((item, index) => (
            <button
              key={index}
              onClick={() => {
                handleNavigation(item.href);
                setMenuOpen(false);
              }}
              className={cn(
                "py-3 px-4 rounded-xl text-base font-medium transition-all duration-200",
                "hover:bg-black/5 active:scale-[0.98]",
                item.badge ? "flex flex-col items-center" : "flex items-center justify-center"
              )}
              role="menuitem"
              aria-label={`Navigate to ${item.name} section`}
            >
              {item.name}
              {item.badge && (
                <span className="text-[11px] font-medium text-black/40 -mt-0.5">
                  {item.badge}
                </span>
              )}
            </button>
          ))}
          <div className="mt-2 pt-4 border-t border-black/10">
            <DownloadButton variant="appstore" singleLine label="Download" />
          </div>
        </div>
      )}
    </nav>
  );
}

function MenuToggle({
  isMenuOpen,
  setMenuOpen
}: {
  isMenuOpen: boolean;
  setMenuOpen: (open: boolean) => void;
}) {
  return (
    <button
      className={cn(
        "text-black min-w-[44px] min-h-[44px] flex items-center justify-center"
      )}
      onClick={() => setMenuOpen(!isMenuOpen)}
      aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
      aria-expanded={isMenuOpen}
      aria-controls="mobile-menu"
    >
      <span aria-hidden="true">
        {!isMenuOpen ? (
          <icons.meanuelines></icons.meanuelines>
        ) : (
          <icons.crossicon></icons.crossicon>
        )}
      </span>
    </button>
  );
}
